import React, { useEffect, useRef, forwardRef } from "react";
import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  CreateLink,
  diffSourcePlugin,
  DiffSourceToggleWrapper,
  headingsPlugin,
  InsertTable,
  InsertThematicBreak,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  ListsToggle,
  MDXEditor,
  MDXEditorMethods,
  quotePlugin,
  StrikeThroughSupSubToggles,
  tablePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
  UndoRedo,
} from "@mdxeditor/editor";
import "@mdxeditor/editor/style.css";
import { PaperProps, Typography } from "@mui/material";
import clsx from "clsx";
import {
  EditorContainer,
  ReadOnlyContainer,
  EditorPaper,
} from "./RichTextEditor.styles";

interface RichTextEditorProps extends Omit<PaperProps, "onChange"> {
  value: string | undefined;
  name?: string;
  id?: string;
  disabled?: boolean;
  readOnly?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<Element, Element>) => void;
  placeholder?: string;
  className?: string;
  maxLines?: number;
  fullWidth?: boolean;
  error?: boolean;
  helperText?: string;
}

export const TABLE_READ_ONLY_MAX_LINES = 3;
export const EDITABLE_MAX_LINES = 10;

const RichTextEditor = forwardRef<MDXEditorMethods, RichTextEditorProps>(
  (
    {
      id,
      name,
      value,
      disabled,
      readOnly,
      onChange,
      onBlur,
      placeholder,
      className,
      maxLines,
      fullWidth,
      error = false,
      helperText,
      ...paper
    },
    ref
  ) => {
    const lastValueRef = useRef<string | undefined>();
    const editorRef = useRef<MDXEditorMethods>(null);
    useEffect(() => {
      if (editorRef.current && value !== lastValueRef.current) {
        editorRef.current.setMarkdown(value ?? "");
        lastValueRef.current = value;
      }
    }, [value]);

    const linkAutocompleteSuggestions = [
      "http://www.rkts.org/",
      "https://www.bdrc.io/",
      "https://www-en.dila.edu.tw/",
      "https://www.cambridgebuddhistcentre.com/",
      "https://www.dila.edu.tw/",
      "https://www.cambridgebuddhistcentre.com/",
      "http://databases.aibs.columbia.edu/",
      "https://suekasueka.com.my/",
      "https://adarshah.org/",
      "https://www.bdrc.io/",
    ];

    const handleChange = (newValue: string) => {
      if (!onChange) return;
      onChange({
        target: { name, value: newValue, type: "rich-text" },
      } as React.ChangeEvent<HTMLTextAreaElement>);
    };

    const handleBlur = (e: any) => {
      if (!onBlur) return;
      onBlur(e as React.FocusEvent<Element, Element>);
    };

    const toolbar = toolbarPlugin({
      toolbarClassName: "mdxeditor-toolbar",
      toolbarContents: () => (
        <DiffSourceToggleWrapper>
          <UndoRedo />
          <BlockTypeSelect />
          <BoldItalicUnderlineToggles />
          <StrikeThroughSupSubToggles />
          <ListsToggle />
          <CreateLink />
          <InsertTable />
          <InsertThematicBreak />
        </DiffSourceToggleWrapper>
      ),
    });

    const plugins = [
      headingsPlugin(),
      quotePlugin(),
      linkDialogPlugin({
        linkAutocompleteSuggestions: linkAutocompleteSuggestions,
      }),
      tablePlugin(),
      thematicBreakPlugin(),
      diffSourcePlugin(),
      listsPlugin(),
      linkPlugin(),
    ];

    // For readonly mode, render a simple markdown renderer instead of the editor
    if (readOnly) {
      return (
        <ReadOnlyContainer
          id={id}
          className={clsx(className, "rich-text-editor", "readonly")}
          maxLines={maxLines}
          {...paper}
          sx={{ width: fullWidth ? "100%" : "auto", ...paper.sx }}
        >
          {value ? (
            <MDXEditor
              ref={editorRef}
              onBlur={handleBlur}
              markdown={value ?? ""}
              onChange={handleChange}
              placeholder={placeholder}
              readOnly={true}
              plugins={plugins}
              className="mdxeditor"
            />
          ) : (
            <Typography color="text.disabled" variant="body2">
              {placeholder || ""}
            </Typography>
          )}
        </ReadOnlyContainer>
      );
    }

    return (
      <EditorContainer
        className="rich-text-editor-container"
        fullWidth={fullWidth}
        error={error}
      >
        <EditorPaper
          id={id}
          className={clsx(
            className,
            "rich-text-editor",
            "editable",
            disabled && "Mui-disabled",
            error && "Mui-error"
          )}
          variant="outlined"
          maxLines={maxLines}
          fullWidth={fullWidth}
          error={error}
        >
          <MDXEditor
            ref={editorRef}
            onBlur={handleBlur}
            markdown={value || ""}
            onChange={handleChange}
            placeholder={placeholder}
            readOnly={disabled}
            plugins={[toolbar, ...plugins]}
            className="mdxeditor"
          />
        </EditorPaper>
        {error && helperText && (
          <Typography className="error-text">{helperText}</Typography>
        )}
        {!error && helperText && (
          <Typography className="helper-text">{helperText}</Typography>
        )}
      </EditorContainer>
    );
  }
);

export { RichTextEditor };
